<template>
  <section>
    <div
      class="eds-toolbar atb-bg-white atb-justify-evenly"
      v-show="useDataSource"
    >
      <div
        id="codeEditorDatasourcesSelect"
        class="toolbar-section"
      >
        <label class="middle-spacing"> {{ $t('dataSources') }} </label>
        <Select
          placeholder="Please Choose..."
          v-model="selectionData.dataSourceSelected"
          :options="dataSourceList"
          @update:model-value="changeDatasource"
          :field-control-class="'long'"
        />
      </div>

      <div
        id="codeEditorSegmentsSelect"
        class="toolbar-section"
      >
        <label class="middleSpacing"> {{ $t('segments') }} </label>
        <Select
          :disabled="
            useDataSource && validateEmpty(selectionData.dataSourceSelected)
          "
          label="id"
          v-model="selectionData.segmentSelected"
          :options="dataSourceSegmentList"
          @update:model-value="changeSegment"
          value="id"
          class=""
          :show-place-holder="false"
        />
      </div>

      <div
        id="codeEditorArgumentsSelect"
        class="toolbar-section"
      >
        <label class="middleSpacing"> {{ $t('arguments') }} </label>
        <Select
          v-model="selectionData.argumentSelected"
          :options="argumentsList"
          @update:model-value="changeArgument"
          :show-place-holder="false"
        />
      </div>
    </div>
    <div class="eds-toolbar atb-bg-white top-border">
      <div
        class="toolbar-section atb-no-padding atb-justify-evenly atb-flex-auto"
      >
        <Button
          id="codeEditorDisplaySearchReplaceModalButton"
          type="button"
          motif="tertiary"
          :title="$t('findAndReplace')"
          @click="displaySearch = true"
        >
          <template #icon>
            <span class="material-icons atb-text-lg">search</span>
          </template>
          {{ $t('findAndReplace') }}
        </Button>
      </div>
      <div
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Textbox
          id="codeEditorGoToLineTextbox"
          v-model="goToLine"
          input-type="number"
          placeholder="Line no"
          :input-style="{ width: '82px' }"
        />
        <Button
          id="codeEditorGoToLineButton"
          type="button"
          motif="tertiary"
          :title="$t('goToLine')"
          @click="goToLineHandler"
        >
          <template #icon>
            <span class="material-icons atb-text-lg"
              >subdirectory_arrow_right</span
            >
          </template>
          {{ $t('goToLine') }}
        </Button>
      </div>
      <div
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Button
          id="codeEditorUndoButton"
          type="button"
          motif="tertiary"
          :title="$t('undo')"
          @click="undoHandler"
        >
          <template #icon>
            <span class="material-icons atb-text-lg">undo</span>
          </template>
        </Button>
        <Button
          id="codeEditorRedoButton"
          type="button"
          motif="tertiary"
          :title="$t('redo')"
          @click="redoHandler"
        >
          <template #icon>
            <span class="material-icons atb-text-lg">redo</span>
          </template>
        </Button>
      </div>
      <div
        id="codeEditorDefaultFontSizeSelect"
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Select
          v-model="defaultFontSize"
          :options="fontSizeList"
        />
      </div>
      <div
        id="codeEditorHighlightSyntaxSwitch"
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Switch
          v-model:checked="highlightSyntax"
          :label="$t('highlightSyntax')"
        />
      </div>
      <div
        id="codeEditorWordWrapSwitch"
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Switch
          v-model:checked="wordWrap"
          :label="$t('wordWrap')"
          @update:checked="onWordWrapChange"
        />
      </div>
      <div
        class="toolbar-section atb-no-padding atb-flex-auto atb-justify-evenly"
      >
        <Button
          id="codeEditorSaveButton"
          type="button"
          motif="tertiary"
          :title="$t('save')"
          @click="saveHandler"
          :disabled="
            (useDataSource &&
              validateEmpty(selectionData.dataSourceSelected)) ||
            loadData.readonly ||
            readonly
          "
        >
          <template #icon>
            <span class="material-icons atb-text-lg">save</span>
          </template>
          {{ $t('save') }}
        </Button>
      </div>
    </div>
    <VAceEditor
      id="codeEditorContentAceEditor"
      v-model:value="code"
      @init="editorInit"
      :lang="language"
      :theme="theme"
      :options="editorOptions"
      :placeholder="placeholder"
      :readonly="readonly || loadData.readonly"
      :wrap="wordWrap"
      :print-margin="printMargin"
      :style="{ minHeight: 'calc(85vh - (245px))' }"
      :hidden="useDataSource && validateEmpty(selectionData.dataSourceSelected)"
    />
    <div class="eds-toolbar atb-bg-white">
      <div class="toolbar-section atb-no-padding atb-flex-auto atb-text-sm">
        <span class="atb-pl-4">{{ $t('position') }} </span>
        <span
          id="codeEditorCurrentLinePositionText"
          class="atb-pl-4"
        >
          {{ $t('ln') }} {{ rowPos }}, {{ $t('ch') }} {{ colPos }}</span
        >
      </div>
    </div>
  </section>
  <Modal
    :show="displaySearch"
    :container-style="{ width: '400px' }"
    body-class="atb-pt-2"
    @onModalClose="searchCloseHandler"
  >
    <template #body>
      <Form>
        <div class="atb-flex atb-flex-col atb-gap-6">
          <FormField :label="$t('Search')">
            <Textbox
              id="codeEditorSearchInputTextbox"
              v-model="searchText"
              :auto-focus="true"
              @update:modelValue="clearFindReplaceMessage"
            />
          </FormField>
          <FormField :label="$t('Replace')">
            <Textbox
              id="codeEditorSearchReplaceTextbox"
              v-model="replaceText"
            />
          </FormField>
          <div class="atb-flex atb-justify-between">
            <Checkbox
              id="codeEditorSearchMatchCaseCheckbox"
              v-model:checked="matchCase"
              :label="$t('matchCase')"
            />
            <Checkbox
              id="codeEditorSearchRegularExpressionCheckbox"
              v-model:checked="regularExpression"
              :label="$t('regularExpression')"
            />
          </div>
        </div>
      </Form>
    </template>
    <template #footer>
      <div class="atb-flex atb-gap-3 atb-pt-0">
        <Button
          id="codeEditorSearchFindNextButton"
          motif="basic"
          @click="findNextClickHandler"
          >{{ $t('findNext') }}</Button
        >
        <Button
          id="codeEditorSearchReplaceButton"
          motif="basic"
          @click="replaceClickHandler"
          >{{ $t('replace') }}</Button
        >
        <Button
          id="codeEditorSearchReplaceAllButton"
          motif="basic"
          @click="replaceAllClickHandler"
          >{{ $t('replaceAll') }}</Button
        >
      </div>
      <div class="atb-pt-0 bottom-border"></div>
      <div class="atb-pt-2">
        <span> {{ findReplaceMessage }} </span>
      </div>
    </template>
  </Modal>
  <Modal
    :show="displayConfirmationModal"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="displayConfirmationModal = false"
  >
    <template
      #header
      class="atb-p-4"
    >
      {{ $t('unsavedChanges') }}
    </template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4">
        {{ $t('areYouSureDatasource') }}
      </div>
    </template>
    <template #footer>
      <Button @click="ignorePendingEditsAndSwitchDatasource">{{
        $t('ignorePendingEditsAndSwitchDatasource')
      }}</Button>
      <Button
        motif="basic"
        @click="cancelDatasourceChange"
        >{{ $t('cancel') }}</Button
      >
    </template>
  </Modal>
</template>

<script lang="ts" setup>
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useDataSourceSegmentStore } from '@/app/shared/stores/datasource-segment-store';
  import { CodeEditorLoadData } from '@/app/shared/types/code-editor-load-data.interface';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { SaveAttributeCodeEvent } from '@/app/shared/types/events/attributes-event.interface';
  import { SaveFilterCodeEvent } from '@/app/shared/types/events/filters-event.interface';
  import { SegmentInfo } from '@/app/shared/types/segment-info.interface';
  import { generateCodeTemplate } from '@/app/shared/utils/code-template-generator.util';
  import { validateEmpty } from '@/app/shared/utils/validation.util';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Select from '@atb-ui/eds-vue/src/components/Select.vue';
  import Switch from '@atb-ui/eds-vue/src/components/Switch.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import type { Ace } from 'ace-builds';
  import { storeToRefs } from 'pinia';
  import { computed, reactive, ref, toRefs, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { VAceEditor } from 'vue3-ace-editor';
  import 'ace-builds/src-noconflict/mode-lua';
  import 'ace-builds/src-noconflict/theme-chrome';

  interface CodeEditorEvents {
    (_event: 'saveClicked', _payload: SaveAttributeCodeEvent): void;
    (_event: 'saveClicked', _payload: SaveFilterCodeEvent): void;
    (_event: 'dataSourceChange', _newDatasource: string): void;
    (_event: 'dataSourceSegmentChange', _payload: SegmentInfo): void;
    (_event: 'setCodeEditorDirtyStatus', booleanField: boolean): void;
  }
  interface CodeEditorProps {
    useDataSource: boolean;
    loadData: CodeEditorLoadData;
    lang: string;
    theme: string;
    options: any;
    placeholder?: string;
    wrap: boolean;
    readonly: boolean;
    printMargin?: boolean | number;
    fontSize?: number;
    fontSizeList?: Array<{ name: string; value: number }>;
  }

  const emit = defineEmits<CodeEditorEvents>();
  const props = withDefaults(defineProps<CodeEditorProps>(), {
    wrap: false,
    printMargin: false,
    fontSize: 12,
    placeholder: '',
    fontSizeList: () => {
      return [
        { name: '8 pt', value: 8 },
        { name: '9 pt', value: 9 },
        { name: '10 pt', value: 10 },
        { name: '11 pt', value: 11 },
        { name: '12 pt', value: 12 },
        { name: '14 pt', value: 14 },
        { name: '16 pt', value: 16 },
      ];
    },
  });

  const { fontSize, wrap, loadData, useDataSource, options, lang } =
    toRefs(props);
  const { t } = useI18n();
  const clientStore = useClientStore();
  const dataSourceSegmentStore = useDataSourceSegmentStore();
  const { selectedClient } = storeToRefs(clientStore);
  const dataSourceList: Array<Datasource> = selectedClient.value.dataSources;

  const argumentsList = ref([
    { name: '0', id: 0 },
    { name: '1', id: 1 },
    { name: '2', id: 2 },
    { name: '3', id: 3 },
    { name: '4', id: 4 },
    { name: '5', id: 5 },
    { name: '6', id: 6 },
    { name: '7', id: 7 },
    { name: '8', id: 8 },
    { name: '9', id: 9 },
  ]);
  let editorRef = ref(null) as unknown as Ace.Editor;
  const defaultFontSize = ref(fontSize.value);
  const displaySearch = ref(false);
  const searchText = ref('');
  const replaceText = ref('');
  const highlightSyntax = ref(true);
  const wordWrap = ref(wrap.value);
  const goToLine = ref(undefined);
  const matchCase = ref(false);
  const regularExpression = ref(false);
  let rowPos = ref(1);
  let colPos = ref(1);
  const codeIsDirty = ref(false);
  const displayConfirmationModal = ref(false);
  const codeToSaved = ref(loadData.value?.code || '');
  const replaceTextCount = ref(0);
  const findReplaceMessage = ref('');

  const code = computed(() => {
    refreshAttributeFilterFunctionLists();
    return loadData.value?.code || generateCodeTemplate(loadData.value);
  });

  const selectionData = reactive({
    dataSourceSelected: '',
    segmentSelected: '',
    argumentSelected: '',
    activeDatasourceSelected: '',
  });

  const refreshAttributeFilterFunctionLists = async () => {
    if (selectedClient !== undefined && selectedClient.value.id != -1) {
      await clientStore.fetchAttributeFilterFunctionList(
        selectedClient.value.id
      );
    }
  };

  const editorOptions = computed(() => {
    return { ...options.value, fontSize: defaultFontSize.value };
  });

  const language = computed(() => {
    return highlightSyntax.value ? lang.value : 'text';
  });

  const onWordWrapChange = (newVal: boolean) => {
    if (editorRef) {
      editorRef.getSession().setUseWrapMode(newVal);
    }
  };

  // After rollback in Filter & use at defineExpose
  const refreshCodeEditorOnRollback = (dataSourceName: string) => {
    selectionData.dataSourceSelected = dataSourceName;
    selectionData.segmentSelected = selectionData.argumentSelected = '';
    refreshDataSourceSegmentList();
    refreshArgumentData();
  };

  const changeDatasource = () => {
    if (codeIsDirty.value) {
      displayConfirmationModal.value = true;
    } else {
      selectionData.activeDatasourceSelected = selectionData.dataSourceSelected;
      displayConfirmationModal.value = false;
      emit('dataSourceChange', selectionData.dataSourceSelected);
      const unwatch = watch(loadData, () => {
        selectionData.segmentSelected = selectionData.argumentSelected = '';
        refreshDataSourceSegmentList();
        refreshArgumentData();
        unwatch();
      });
    }
  };

  const dataSourceSegmentList = computed(() => {
    return loadData.value.availableSegments;
  });

  const refreshDataSourceSegmentList = async () => {
    if (dataSourceSegmentList.value?.length) {
      let segmentId = '';
      if (
        loadData.value?.segment === null &&
        selectionData.segmentSelected === ''
      ) {
        segmentId = dataSourceSegmentList?.value[0].id;
      } else if (selectionData.segmentSelected !== '') {
        segmentId = selectionData.segmentSelected;
      } else if (loadData.value?.segment !== null) {
        segmentId = loadData.value?.segment!;
      }
      selectionData.segmentSelected = segmentId!;
      refreshSegmentRawData(segmentId);
    }
  };

  const refreshSegmentRawData = (newSegmentId: string) => {
    emit('dataSourceSegmentChange', {
      dataSourceName: selectionData.dataSourceSelected,
      segmentId: newSegmentId,
    } as SegmentInfo);
  };

  const refreshArgumentData = () => {
    let argumentName = '';
    if (loadData.value?.arguments === null) {
      argumentName = argumentsList.value[0].name;
    } else {
      argumentName = loadData.value?.arguments!;
    }
    selectionData.argumentSelected = argumentName!;
  };

  const changeSegment = () => {
    refreshDataSourceSegmentList();
    setDirtyField(true);
  };

  const changeArgument = () => {
    setDirtyField(true);
  };

  const ignorePendingEditsAndSwitchDatasource = () => {
    displayConfirmationModal.value = false;
    setDirtyField(false);
    changeDatasource();
  };

  const cancelDatasourceChange = () => {
    selectionData.dataSourceSelected = selectionData.activeDatasourceSelected;
    displayConfirmationModal.value = false;
  };

  const undoHandler = () => {
    editorRef.undo();
  };

  const redoHandler = () => {
    editorRef.redo();
  };

  const findNextClickHandler = () => {
    editorRef.findNext({
      needle: searchText.value,
      caseSensitive: matchCase.value,
      regExp: regularExpression.value,
    });
    validateSearchField();
  };

  const replaceClickHandler = () => {
    findNextClickHandler();
    editorRef.replace(replaceText.value, {
      needle: searchText.value,
      caseSensitive: matchCase.value,
      regExp: regularExpression.value,
    });
  };

  const replaceAllClickHandler = () => {
    findNextClickHandler();
    replaceTextCount.value = editorRef.replaceAll(replaceText.value, {
      needle: searchText.value,
      caseSensitive: matchCase.value,
      regExp: regularExpression.value,
    });
    if (replaceTextCount.value > 0) {
      findReplaceMessage.value =
        replaceTextCount.value + t('occurrencesReplaced');
    }
  };

  const validateSearchField = () => {
    clearFindReplaceMessage();
    if (validateEmpty(searchText.value)) {
      findReplaceMessage.value = t('searchFieldEmpty');
    } else if (!editorRef.getSelectedText()) {
      findReplaceMessage.value = searchText.value + t('notFound');
    }
  };

  const clearFindReplaceMessage = () => {
    findReplaceMessage.value = '';
  };

  const goToLineHandler = () => {
    goToLine.value && editorRef.gotoLine(Number(goToLine.value), 0, false);
    goToLine.value = undefined;
  };

  const searchCloseHandler = () => {
    displaySearch.value = false;
    searchText.value = '';
    replaceText.value = '';
    matchCase.value = false;
    regularExpression.value = false;
    findReplaceMessage.value = '';
  };

  const saveHandler = () => {
    if (useDataSource.value) {
      const selectedDs = dataSourceList
        .filter(
          (currentDs) => currentDs.name === selectionData.dataSourceSelected
        )
        .shift();
      emit('saveClicked', {
        code: codeToSaved.value,
        dataSourceId: selectedDs?.id,
        segment: selectionData.segmentSelected,
        filterArguments: Number(selectionData.argumentSelected),
      } as SaveFilterCodeEvent);
    } else {
      emit('saveClicked', {
        code: codeToSaved.value,
      } as SaveAttributeCodeEvent);
    }
  };

  const onEditChangeHandler = (editor: Ace.Editor) => {
    codeToSaved.value = editor.getValue();
    if (codeToSaved.value === code.value) {
      setDirtyField(false);
    } else {
      setDirtyField(true);
    }
  };

  const onCursorChangeHandler = (pos: Ace.Point) => {
    rowPos.value = pos.row >= 0 ? pos.row + 1 : rowPos.value;
    colPos.value = pos.column >= 0 ? pos.column + 1 : colPos.value;
  };

  const setDirtyField = (isDirty: boolean) => {
    codeIsDirty.value = isDirty;
    emit('setCodeEditorDirtyStatus', codeIsDirty.value);
  };

  const editorInit = (editor: any) => {
    editorRef = editor;

    editor.on('change', () => {
      onEditChangeHandler(editor);
    });

    editor.selection.on('changeCursor', () => {
      onCursorChangeHandler(editor?.getCursorPosition());
    });
  };

  defineExpose({
    refreshCodeEditorOnRollback,
  });
</script>
<style scoped>
  .middle-spacing {
    margin: 0 15px 0 15px;
  }
  .top-border {
    border-top-width: 1px;
    border-color: rgba(214, 214, 214, var(--border-opacity));
  }
  .bottom-border {
    display: flex;
    align-items: stretch;
    padding: 20px;
    border-bottom: 2px solid #dee2e6;
  }
  /* On screens that are 1440ppx or more*/
  @media screen and (min-width: 1025px) {
    .long {
      min-width: 400px;
    }
  }
</style>
